<template>
  <v-form ref="IntegrationMonitoringFilterForm" class="fill-width">
    <ExpandableFilterCard
      class="integration-monitoring-filter"
      elevation="2"
      :loading="loadingPage || loadingTable"
      :search-disabled="!canSearch"
      :auto-search-on-always-visible-change="true"
      :always-visible-values="alwaysVisibleValues"
      :expand-button-hover-text-collapsed="$t('integrationMonitoring.filter.expandSearch')"
      :expand-button-hover-text-expanded="$t('integrationMonitoring.filter.collapseSearch')"
      :search-label="$t('integrationMonitoring.filter.search')"
      :clear-label="$t('integrationMonitoring.filter.clear')"
      @on-search="searchRecords"
      @on-clear="resetFilters">
      <template v-slot:always-visible>
        <div class="integration-monitoring-filter__fields">
          <v-select
            :label="$t('integrationMonitoring.filter.integrationType')"
            :items="integrationTypeOptions"
            :value="filter.integrationType"
            item-text="text"
            item-value="value"
            item-disabled="disabled"
            outlined
            hide-details
            @input="onIntegrationTypeChange"
          />

          <v-select
            :label="$t('integrationMonitoring.filter.processType')"
            :items="processTypeOptions"
            :value="filter.processType"
            item-text="text"
            item-value="value"
            item-disabled="disabled"
            outlined
            hide-details
            @input="onProcessTypeChange"
          />

          <v-menu
            ref="periodMenu"
            v-model="periodMenuOpen"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="periodRangeText"
                :label="$t('integrationMonitoring.filter.receivedPeriod')"
                :placeholder="$t('integrationMonitoring.filter.placeholders.period')"
                readonly
                outlined
                hide-details
                clearable
                v-bind="attrs"
                v-on="on"
                @click:clear="clearPeriod"
              />
            </template>
            <v-date-picker
              v-model="periodDates"
              range
              no-title
              color="primary"
              @input="onPeriodPickerInput">
              <v-spacer />
              <v-btn
                text
                color="primary"
                @click="periodMenuOpen = false">
                {{ $t('common.cancel') }}
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="confirmPeriod">
                OK
              </v-btn>
            </v-date-picker>
          </v-menu>

          <PaginatedAutocomplete
            :label="$t('integrationMonitoring.filter.financialGroup')"
            :fetch="fetchFinancialGroups"
            :value="filter.financialGroupId"
            item-text="name"
            item-value="id"
            v-bind="autocompleteAttrs"
            @input="onFinancialGroupChange"
          />
        </div>
      </template>

      <template v-slot:expanded>
        <div class="integration-monitoring-filter__expanded">
          <PaginatedAutocomplete
            :key="`carrier-${filter.financialGroupId || 'none'}`"
            :label="$t('integrationMonitoring.filter.carrier')"
            :fetch="fetchCarriers"
            :disabled="!filter.financialGroupId"
            :value="filter.carrierId"
            item-text="name"
            item-value="id"
            v-bind="autocompleteAttrs"
            @input="onCarrierChange"
          />

          <PaginatedAutocomplete
            :key="`contract-${filter.financialGroupId || 'none'}`"
            :label="$t('integrationMonitoring.filter.contract')"
            :fetch="fetchContracts"
            :disabled="!filter.financialGroupId"
            :value="filter.contractId"
            item-text="name"
            item-value="id"
            v-bind="autocompleteAttrs"
            @input="onContractChange"
          />

          <v-select
            class="integration-monitoring-filter__status-select"
            :label="$t('integrationMonitoring.filter.status')"
            :items="statusOptions"
            :value="filter.status"
            item-text="text"
            item-value="value"
            multiple
            chips
            small-chips
            deletable-chips
            outlined
            hide-details
            :placeholder="$t('integrationMonitoring.filter.placeholders.select')"
            @input="onStatusChange"
          />
        </div>
      </template>
    </ExpandableFilterCard>
  </v-form>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

import ExpandableFilterCard from '@/components/Default/ExpandableFilterCard.vue';
import PaginatedAutocomplete from '@/components/Default/PaginatedAutocomplete.vue';
import IntegrationMonitoringService from '@/services-http/servicos/IntegrationMonitoringService';
import {
  INTEGRATION_TYPE_OPTIONS,
  PROCESS_TYPE_OPTIONS,
  REQUEST_STATUS_OPTIONS,
} from '@/store/modules/integration-monitoring/integration-monitoring-config';

const PROCESS_TYPE_I18N_KEYS = {
  COPARTICIPATION: 'coparticipation',
  CARRIER_CADASTRAL_MOVEMENT: 'carrierCadastralMovement',
  CLIENT_CADASTRAL_MOVEMENT: 'clientCadastralMovement',
  PAYROLL: 'payroll',
};

const INTEGRATION_TYPE_I18N_KEYS = {
  API: 'api',
  RPA: 'rpa',
};

export default {
  name: 'IntegrationMonitoringFilter',
  components: { ExpandableFilterCard, PaginatedAutocomplete },
  data: () => ({
    periodMenuOpen: false,
    periodDates: [],
    monitoringService: null,
  }),
  computed: {
    ...mapGetters({
      filter: 'integration-monitoring/filter',
      loadingPage: 'integration-monitoring/loadingPage',
      loadingTable: 'integration-monitoring/loadingTable',
      canSearch: 'integration-monitoring/canSearch',
    }),
    alwaysVisibleValues() {
      return {
        integrationType: this.filter.integrationType,
        processType: this.filter.processType,
        periodStart: this.filter.periodStart,
        periodEnd: this.filter.periodEnd,
        financialGroupId: this.filter.financialGroupId,
      };
    },
    integrationTypeOptions() {
      return INTEGRATION_TYPE_OPTIONS.map((option) => ({
        ...option,
        text: this.$t(`integrationMonitoring.filter.integrationTypes.${INTEGRATION_TYPE_I18N_KEYS[option.value]}`),
      }));
    },
    processTypeOptions() {
      return PROCESS_TYPE_OPTIONS.map((option) => ({
        ...option,
        text: this.$t(`integrationMonitoring.filter.processTypes.${PROCESS_TYPE_I18N_KEYS[option.value]}`),
      }));
    },
    statusOptions() {
      return REQUEST_STATUS_OPTIONS.map((status) => ({
        value: status,
        text: this.$t(`integrationMonitoring.filter.requestStatus.${status}`),
      }));
    },
    periodRangeText() {
      if (!this.filter.periodStart || !this.filter.periodEnd) {
        return '';
      }

      const start = moment(this.filter.periodStart).format('DD/MM/YYYY');
      const end = moment(this.filter.periodEnd).format('DD/MM/YYYY');

      return `${start} ~ ${end}`;
    },
    autocompleteAttrs() {
      return {
        clearable: true,
        regular: true,
        color: 'primary',
        itemColor: 'primary',
        placeholder: this.$t('integrationMonitoring.filter.placeholders.select'),
        persistentPlaceholder: false,
        borderSelected: true,
        hideDetails: true,
        groupByHeader: false,
        minSearchChars: 0,
      };
    },
  },
  watch: {
    'filter.periodStart': {
      immediate: true,
      handler(start) {
        if (start && this.filter.periodEnd) {
          this.periodDates = [start, this.filter.periodEnd];
        } else if (!start && !this.filter.periodEnd) {
          this.periodDates = [];
        }
      },
    },
    'filter.periodEnd': {
      immediate: true,
      handler(end) {
        if (this.filter.periodStart && end) {
          this.periodDates = [this.filter.periodStart, end];
        } else if (!this.filter.periodStart && !end) {
          this.periodDates = [];
        }
      },
    },
  },
  methods: {
    ...mapActions({
      applyFilter: 'integration-monitoring/applyFilter',
      resetFilter: 'integration-monitoring/resetFilter',
      searchByFilter: 'integration-monitoring/searchByFilter',
    }),
    onIntegrationTypeChange(value) {
      this.applyFilter({ integrationType: value });
    },
    onProcessTypeChange(value) {
      this.applyFilter({ processType: value });
    },
    onFinancialGroupChange(financialGroupId) {
      this.applyFilter({
        financialGroupId: financialGroupId || null,
        carrierId: null,
        contractId: null,
      });
    },
    onCarrierChange(carrierId) {
      this.applyFilter({ carrierId: carrierId || null });
    },
    onContractChange(contractId) {
      this.applyFilter({ contractId: contractId || null });
    },
    onStatusChange(status) {
      this.applyFilter({ status: status || [] });
    },
    onPeriodPickerInput(dates) {
      this.periodDates = dates;
    },
    confirmPeriod() {
      this.periodMenuOpen = false;

      if (!Array.isArray(this.periodDates) || this.periodDates.length < 2) {
        return;
      }

      const sortedDates = [...this.periodDates].sort(
        (first, second) => new Date(first) - new Date(second),
      );

      this.applyFilter({
        periodStart: sortedDates[0],
        periodEnd: sortedDates[1],
      });
    },
    clearPeriod() {
      this.periodDates = [];
      this.applyFilter({ periodStart: null, periodEnd: null });
    },
    async fetchFinancialGroups(page, size, searchText) {
      return this.monitoringService.fetchFinancialGroups(page, size, searchText);
    },
    async fetchCarriers(page, size, searchText) {
      return this.monitoringService.fetchCarriers(
        page,
        size,
        searchText,
        this.filter.financialGroupId,
      );
    },
    async fetchContracts(page, size, searchText) {
      return this.monitoringService.fetchContracts(
        page,
        size,
        searchText,
        this.filter.financialGroupId,
      );
    },
    async searchRecords() {
      if (!this.canSearch) return;
      await this.searchByFilter();
    },
    async resetFilters() {
      await this.resetFilter();
      this.periodDates = [];
    },
  },
  created() {
    this.monitoringService = new IntegrationMonitoringService();
  },
};
</script>

<style lang="scss">
@import "@/shared/styles/integrationMonitoring/filter.scss";
</style>
